import { useQuery } from "@hooks/useQuery";
import { DatePicker, Pagination, TableColumnsType } from "antd";
import { ColumnType } from "antd/lib/table";
import { AxiosInstance } from "axios";
import { debounce } from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import { useNotification } from "~/Hooks/useNotification";
import { eramanBEApi } from "~/Services";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";

const { RangePicker } = DatePicker;

// Helper function to fetch all data using totalRows
export const fetchAllData = async (apiUrl: string, totalRows: number, apiClient: AxiosInstance): Promise<any[]> => {
  const url = `${apiUrl}&pageNo=1&pageSize=${totalRows}`;
  const response = await apiClient.get(url);
  if (!response.data?.data) {
    throw new Error("No data received from API");
  }
  return response.data.data;
};

// Helper function to determine if a column is a ColumnType (not a group)
export function isColumnType<T>(column: ColumnType<T> | any): column is ColumnType<T> {
  return "dataIndex" in column;
}

// Updated createExportHandler
export const createExportHandler = async (
  apiClient: AxiosInstance,
  apiUrl: string,
  columns: TableColumnsType<any>,
  exportFileName: string,
  selectedKeys: React.Key[] = [],
  totalRows?: number
): Promise<void> => {
  try {
    let exportData;
    if (totalRows && selectedKeys.length === 0) {
      exportData = await fetchAllData(apiUrl, totalRows, apiClient);
    } else {
      const response = await apiClient.get(apiUrl);
      if (!response.data?.data) {
        throw new Error("No data received from API");
      }
      exportData = response.data.data;
    }

    if (selectedKeys.length > 0) {
      exportData = exportData.filter((row: any) => selectedKeys.includes(row.id));
    }

    const exportColumns = columns.filter(
      (col): col is ColumnType<any> =>
        isColumnType(col) && typeof col.dataIndex === "string" && col.dataIndex !== "actions"
    );

    const dateFields = ["subscribedAt", "unsubscribedAt"];

    const formattedData = exportData.map((record: any) => {
      const row: { [key: string]: any } = {};
      exportColumns.forEach((col) => {
        const dataIndex = col.dataIndex as string;
        const title = col.title as string;

        let value = record[dataIndex];
        if (col.render && typeof col.render === "function") {
          try {
            const renderedValue = col.render(value, record, 0);
            if (typeof renderedValue !== "object") {
              value = renderedValue;
            }
          } catch {
            // ignore render failures
          }
        }

        if (dateFields.includes(dataIndex) && value) {
          value = moment(value).format("YYYY-MM-DD HH:mm:ss");
        }
        row[title] = value ?? "";
      });

      return row;
    });

    if (formattedData.length > 0) {
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportFileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      throw new Error("No data selected for export");
    }
  } catch (error) {
    console.error("Error exporting data:", error);
    throw error;
  }
};

export const NewsLetterSubscription = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("subscribedAt");
  const [selectedSubscribedType, setSelectedSubscribedType] = useState<any>("");
  const [startDateType, setStartDateType] = useState<any>("");
  const [endDateType, setEndDateType] = useState<any>("");
  const [pageSize, setPageSize] = useState<number>(10);
  const { openToast } = useNotification();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectionStarted, setSelectionStarted] = useState(false);

  const subscribedType: TypeData[] = [
    { label: "Subscribed", value: "Subscribed" },
    { label: "Unsubscribed", value: "Unsubscribed" },
  ];

  const getRecords = useCallback(
    (
      sortOrderParam: string,
      orderByParam: string,
      searchStringParam: string,
      currentPageParam: number,
      pageSizeParam: number
    ) => {
      setIsLoading(true);
      const url = `/admin/subscriptions?pageNo=${currentPageParam ?? ""}&pageSize=${pageSizeParam || 10}&sortBy=${orderByParam ?? ""}&sortOrder=${sortOrderParam ?? ""}&type=${selectedSubscribedType ?? ""}&startDate=${startDateType ?? ""}&endDate=${endDateType ?? ""}&search=${searchStringParam ?? ""}&isMember=false`;

      eramanBEApi
        .get(url)
        .then((res: any) => {
          if (res && res.data) {
            setTotalRecords(res.data.totalRows);
            setData(res.data.data);
          }
        })
        .catch((err) => {
          setData([]);
          console.error("Error fetching data:", err);
        })
        .finally(() => setIsLoading(false));
    },
    [selectedSubscribedType, startDateType, endDateType]
  );

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, currentPage, pageSize);
  }, [getRecords, sortOrder, orderByName, searchString, currentPage, pageSize]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
    setCurrentPage(1);
  }, 1000);

  const columns: TableColumnsType = [
    {
      title: "Email",
      dataIndex: "emailAddress",
      key: "emailAddress",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "emailAddress"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "emailAddress"
            ? "descend"
            : undefined,
    },
    {
      title: "Subscription Status",
      dataIndex: "subscription",
      key: "subscription",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subscription"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subscription"
            ? "descend"
            : undefined,
    },
    {
      title: "Subscription Date",
      dataIndex: "subscribedAt",
      key: "subscribedAt",
      render: (subscribedAt: string) => moment(subscribedAt).format("DD MMM YYYY hh:mm:ss A"),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subscribedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subscribedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Unsubscription Date",
      dataIndex: "unsubscribedAt",
      key: "unsubscribedAt",
      render: (unsubscribedAt: string) =>
        unsubscribedAt ? moment(unsubscribedAt).format("DD MMM YYYY hh:mm:ss A") : "",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "unsubscribedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "unsubscribedAt"
            ? "descend"
            : undefined,
    },
  ];

  async function handleExportData(isExportAll: boolean) {
    try {
      const baseApiUrl = `/admin/subscriptions?sortBy=${orderByName ?? ""}&sortOrder=${sortOrder ?? ""}&type=${selectedSubscribedType ?? ""}&startDate=${startDateType ?? ""}&endDate=${endDateType ?? ""}&search=${searchString ?? ""}&isMember=false`;
      const apiUrl = isExportAll ? baseApiUrl : `${baseApiUrl}&pageNo=${currentPage}&pageSize=${pageSize || 10}`;

      await createExportHandler(
        eramanBEApi,
        apiUrl,
        columns,
        "GUEST_USERS",
        isExportAll ? [] : selectedRowKeys,
        isExportAll ? totalRecords : undefined
      );
    } catch {
      openToast({ content: "Failed to export data", type: "error" });
    }
  }

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        className="table-clickable"
        tableData={data ? data.map((d: any) => ({ ...d, key: d.id })) : []}
        isLoading={isLoading}
        exportFileName="GUEST_USERS"
        exportHandler={handleExportData}
        totalRecords={totalRecords}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        isPagination={true}
        recordsPerPage={10}
        multiSelect={true}
        hideExport={false}
        onClick={(record) => setSelectedRowData(record)}
        isServerSearchApi={true}
        searchPlaceholderText={"Search Email"}
        onSearchChange={handleSearchChange}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <div className="relative w-80">
              <RangePicker
                value={[startDateType ? moment(startDateType) : null, endDateType ? moment(endDateType) : null]}
                onChange={(dates, dateStrings) => {
                  const [start, end] = dateStrings;
                  setStartDateType(start ? moment(start).format("YYYY-MM-DD") : "");
                  setEndDateType(end ? moment(end).format("YYYY-MM-DD") : "");
                  setCurrentPage(1);
                  if (!start && !end) setSelectionStarted(false);
                }}
                onCalendarChange={(dates) => {
                  if (dates && dates[0]) setSelectionStarted(true);
                }}
                format="YYYY-MM-DD"
                placeholder={["", ""]}
                allowEmpty={[true, true]}
                className="w-full"
                onKeyDown={(e) => e.preventDefault()}
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf("day");
                  if (current > today) return true;
                  if (startDateType && !endDateType) {
                    return current < moment(startDateType, "YYYY-MM-DD");
                  }
                  if (!startDateType && endDateType) {
                    return current > moment(endDateType, "YYYY-MM-DD");
                  }
                  return false;
                }}
              />

              {/* white-background placeholder overlay */}
              {!(selectionStarted || startDateType || endDateType) && (
                <span
                  style={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "#ffffff",
                    padding: "0 8px",
                    zIndex: 2,
                    pointerEvents: "none",
                    color: "#9CA3AF",
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Select Subscription Start & End Dates
                </span>
              )}
            </div>

            <ActionDropdown
              value={selectedSubscribedType}
              options={subscribedType}
              onChange={(value) => {
                setSelectedSubscribedType(value);
                setCurrentPage(1);
              }}
              placeholder="Select Subscription Status"
            />
          </div>
        }
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>
    </>
  );
};
